import type {
  BrowserCacheLocation,
  Configuration,
  IPublicClientApplication,
} from '@azure/msal-browser';

import { webEnv, type MicrosoftSettings } from '../lib/env';

/**
 * The Microsoft Entra ID sign-in mechanism (ADR-29, ADR-33). Nothing else in the
 * portal imports `@azure/msal-browser`, except the redirect bridge page
 * (`redirect-bridge.ts`), which imports only `@azure/msal-browser/redirect-bridge`.
 *
 * MSAL is a login mechanism here and nothing more. It opens Microsoft's own hosted
 * sign-in page in a popup, receives the ID token, and hands that token to the auth
 * service, which exchanges it at `POST /api/auth/microsoft` for a Prisco session.
 * The token is then spent:
 *
 *  - the cache is memory-only, so no Microsoft credential is ever written to
 *    `localStorage` or `sessionStorage` (ADR-33);
 *  - `acquireTokenSilent`, `ssoSilent` and `getTokenCache()` are never called,
 *    because the API accepts no Microsoft token after the exchange;
 *  - Prisco sign-out clears this cache but never calls `logoutRedirect()`,
 *    which would sign the person out of every Microsoft application.
 *
 * Why a popup: MSAL v5 refuses `loginRedirect` with a memory-only cache
 * (`in_mem_redirect_unavailable`), because a full-page redirect unloads the memory
 * that holds the PKCE verifier. A popup keeps this page — and its memory — alive.
 * Microsoft returns the popup to the bridge page at {@link MICROSOFT_REDIRECT_PATH},
 * which posts the response back to this page over a `BroadcastChannel`.
 *
 * The library is loaded on demand, so a visitor who is already signed in (session
 * restored from the refresh cookie) never downloads it.
 */

/** ADR-33 rule 2: exactly these scopes, nothing else. */
export const MICROSOFT_LOGIN_SCOPES: readonly string[] = ['openid', 'profile', 'email'];

/**
 * The redirect bridge page (`apps/web/redirect.html`). It must be registered in the
 * Entra app as a **Single-page application** redirect URI, e.g.
 * `http://localhost:5173/redirect.html`, and served without a
 * `Cross-Origin-Opener-Policy` header.
 */
export const MICROSOFT_REDIRECT_PATH = '/redirect.html';

export type MicrosoftSignInFailureKind =
  /** `VITE_MICROSOFT_CLIENT_ID` is missing in this build. */
  | 'not-configured'
  /** The user closed the popup or backed out on Microsoft's page. */
  | 'cancelled'
  /** The browser blocked the sign-in popup. */
  | 'popup-blocked'
  /** A previous sign-in is still pending in this tab. */
  | 'in-progress'
  /** Microsoft reported an error, or the response could not be processed. */
  | 'failed';

/** A failure on the Microsoft side of sign-in, before Prisco is involved. */
export class MicrosoftSignInError extends Error {
  public readonly kind: MicrosoftSignInFailureKind;

  constructor(kind: MicrosoftSignInFailureKind) {
    super(`Microsoft sign-in failed: ${kind}`);
    this.name = 'MicrosoftSignInError';
    this.kind = kind;
  }
}

export function isMicrosoftSignInError(value: unknown): value is MicrosoftSignInError {
  return value instanceof MicrosoftSignInError;
}

/**
 * The MSAL configuration, exactly as ADR-33 fixes it. Any other value is a defect:
 *
 * - `cache.cacheLocation` is memory-only;
 * - `storeAuthStateInCookie` does not exist in v5 and is not added;
 * - `system.allowRedirectInIframe` is left at its default (`false`).
 */
export function buildMsalConfiguration(settings: MicrosoftSettings, origin: string): Configuration {
  return {
    auth: {
      clientId: settings.clientId,
      authority: `https://login.microsoftonline.com/${encodeURIComponent(settings.tenant)}`,
      redirectUri: `${origin}${MICROSOFT_REDIRECT_PATH}`,
    },
    cache: {
      // `BrowserCacheLocation.MemoryStorage`, checked against MSAL's own constant.
      // Spelled as its value so this module needs no runtime import of MSAL.
      cacheLocation: 'memoryStorage' satisfies typeof BrowserCacheLocation.MemoryStorage,
    },
  };
}

/**
 * Removes MSAL's temporary keys from `sessionStorage` before the client exists.
 *
 * MSAL keeps its "interaction in progress" flag there whatever the cache location,
 * and the flag survives a reload. A popup sign-in lives entirely in this page's
 * memory, so any `msal.` key present before this page created its client is left
 * over — from an abandoned attempt, an earlier redirect build or another client
 * id — and would block every sign-in in the tab with `interaction_in_progress`.
 */
function clearLeftoverMsalState(): void {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith('msal.')) {
        window.sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Storage unavailable: MSAL cannot have left anything there either.
  }
}

let clientPromise: Promise<IPublicClientApplication> | null = null;
let clientCreated = false;

function loadClient(): Promise<IPublicClientApplication> {
  const settings = webEnv.microsoft;

  if (settings === null) {
    return Promise.reject(new MicrosoftSignInError('not-configured'));
  }

  clientPromise ??= (async () => {
    const { PublicClientApplication } = await import('@azure/msal-browser');

    clearLeftoverMsalState();
    const client = new PublicClientApplication(
      buildMsalConfiguration(settings, window.location.origin),
    );

    await client.initialize();
    clientCreated = true;

    return client;
  })().catch((error: unknown) => {
    clientPromise = null;
    throw error;
  });

  return clientPromise;
}

/**
 * Maps an MSAL error to the portal's vocabulary. The error code is Microsoft's; its
 * message can carry correlation data and is never shown.
 */
function toSignInError(error: unknown): MicrosoftSignInError {
  if (isMicrosoftSignInError(error)) {
    return error;
  }

  const code =
    typeof error === 'object' && error !== null && 'errorCode' in error
      ? String((error as { readonly errorCode: unknown }).errorCode)
      : '';

  if (import.meta.env.DEV) {
    // Development only: the code (e.g. `invalid_request`, `timed_out`) and any
    // `AADSTS` number, never the full message, which carries correlation data.
    // Production shows nothing.
    const aadsts = error instanceof Error ? (/AADSTS\d+/u.exec(error.message)?.[0] ?? '') : '';
    console.warn(
      `Microsoft sign-in failed: ${code === '' ? String(error) : code} ${aadsts}`.trim(),
    );
  }

  switch (code) {
    case 'access_denied':
    case 'user_cancelled':
      return new MicrosoftSignInError('cancelled');
    case 'popup_window_error':
    case 'empty_window_error':
      return new MicrosoftSignInError('popup-blocked');
    case 'interaction_in_progress':
      return new MicrosoftSignInError('in-progress');
    default:
      return new MicrosoftSignInError('failed');
  }
}

/**
 * Loads and initialises MSAL ahead of the click, so the popup opens inside the
 * user's gesture instead of after a network round trip (browsers block popups
 * opened too long after the click). Called when the sign-in page shows the button.
 * Failures are ignored here; the click reports them.
 */
export function prepareMicrosoftLogin(): void {
  if (webEnv.microsoft === null) {
    return;
  }

  loadClient().catch(() => undefined);
}

/**
 * Signs in with Microsoft in a popup and resolves the ID token. MSAL's memory cache
 * is cleared at once: after this call no Microsoft artefact remains in the page
 * except the token string the caller is about to post.
 */
export async function signInWithMicrosoftPopup(): Promise<string> {
  try {
    const client = await loadClient();
    // `select_account`: always show Microsoft's account picker, so a browser already
    // signed in to Microsoft (e.g. the Azure portal) can still choose another account.
    const result = await client.loginPopup({
      scopes: [...MICROSOFT_LOGIN_SCOPES],
      prompt: 'select_account',
    });
    const idToken = result.idToken;

    await client.clearCache();

    if (idToken === '') {
      throw new MicrosoftSignInError('failed');
    }

    return idToken;
  } catch (error) {
    throw toSignInError(error);
  }
}

/**
 * ADR-33 rule 4: Prisco sign-out clears MSAL's memory cache and nothing more.
 * `logoutRedirect()` is never called. When MSAL was never loaded in this page there
 * is nothing to clear, so it is not loaded just to be emptied.
 */
export async function clearMicrosoftCache(): Promise<void> {
  if (!clientCreated || clientPromise === null) {
    return;
  }

  try {
    await (await clientPromise).clearCache();
  } catch {
    // The cache is in memory and dies with the page; a failure here changes nothing.
  }
}
